import { z } from 'zod';

/**
 * A tulpa/talent version string as produced by ARX. The bundle spec
 * nominally calls this "SemVer", but real ARX tulpas carry a
 * content-hash here (e.g. a 64-char sha256), and talents inside a
 * bundle can ship `0.0.0` placeholders. ARX is the validator of record,
 * so the marketplace must accept whatever version string ARX accepted —
 * it must not re-impose a stricter format than the source of truth.
 */
const VersionString = z.string().min(1).max(128);

const SlugString = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase alphanumeric with hyphens');

/** Talent reference embedded in a tulpa version (slug → version+sha). */
export const TulpaTalentRef = z.object({
  slug: z.string(),
  version: VersionString,
  sha256: z.string(),
});
export type TulpaTalentRef = z.infer<typeof TulpaTalentRef>;

export const TulpaSummary = z.object({
  id: z.string(),
  slug: SlugString,
  name: z.string(),
  summary: z.string(),
  owner_id: z.string(),
  owner_name: z.string().nullable(),
  engines: z.array(z.string()),
  default_model: z.string(),
  hosting_mode: z.string(),
  latest_version: VersionString.nullable(),
  talent_count: z.number().int().nonnegative(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type TulpaSummary = z.infer<typeof TulpaSummary>;

export const TulpaDetail = TulpaSummary.extend({
  description: z.string(),
  license: z.string(),
  persona_excerpt: z.string(),
  talents: z.array(TulpaTalentRef),
  versions: z.array(
    z.object({
      id: z.string(),
      version: VersionString,
      size_bytes: z.number().int().nonnegative(),
      sha256: z.string(),
      created_at: z.string(),
    }),
  ),
});
export type TulpaDetail = z.infer<typeof TulpaDetail>;

export const CreateTulpaBody = z.object({
  slug: SlugString,
  name: z.string().min(1).max(200),
  summary: z.string().max(500).default(''),
});
export type CreateTulpaBody = z.infer<typeof CreateTulpaBody>;

export const UpdateTulpaBody = z.object({
  name: z.string().min(1).max(200).optional(),
  summary: z.string().max(500).optional(),
  description: z.string().max(20000).optional(),
});
export type UpdateTulpaBody = z.infer<typeof UpdateTulpaBody>;

export const ListTulpasQuery = z.object({
  q: z.string().optional(),
  engine: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(20),
});
export type ListTulpasQuery = z.infer<typeof ListTulpasQuery>;
